import React, { useRef, useState } from 'react';
import { Button, Space, message } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addCanvasToPdf } from '../utils/canvasPdf';

/**
 * 出境游英文行程单（过海关用）：查看 + 下载 PDF。
 * 数据来自通知书接口的 itinerary 字段（backend/utils/englishItinerary.js）。
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// 'YYYY-MM-DD' → 'Wed 14 Oct 2026'（按日期本身计算，不受浏览器时区影响）
const fmtDate = (iso, withWeekday = true) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${withWeekday ? `${wd} ` : ''}${d} ${MONTHS[m - 1]} ${y}`;
};

const C = {
  primary: '#1a5490',
  border: '#d9d9d9',
  muted: '#666',
  headBg: '#f0f5ff',
};

const cell = { border: `1px solid ${C.border}`, padding: '8px 10px', verticalAlign: 'top', textAlign: 'left' };
const head = { ...cell, background: C.headBg, color: C.primary, fontWeight: 600, whiteSpace: 'nowrap' };

const TravelItinerary = ({ data, onBack, isMobile }) => {
  const docRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  // 截图期间按桌面版式渲染（表格布局），手机上下载的 PDF 与电脑上一致
  const [capturing, setCapturing] = useState(false);
  const compact = isMobile && !capturing;

  const { tourist = {}, itinerary, notice } = data || {};
  const days = itinerary?.days || [];
  const flights = [
    ...(notice?.flightInfo?.outbound || []),
    ...(notice?.flightInfo?.inbound || []),
  ];
  const travellerName = tourist.passportName || tourist.name || '';

  const handleDownload = async () => {
    if (!docRef.current || downloading) return;
    setDownloading(true);
    const el = docRef.current;
    const original = el.getAttribute('style');
    try {
      setCapturing(true);
      await new Promise((r) => setTimeout(r, 50));
      // 固定 A4 宽度截图，与设备宽度无关
      el.style.width = '794px';
      el.style.maxWidth = '794px';
      el.style.boxShadow = 'none';
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(el, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794,
      });
      const pdf = new jsPDF('p', 'mm', 'a4');
      addCanvasToPdf(pdf, canvas, { margin: 8 });
      const safeName = travellerName.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'Traveller';
      const fileName = `Travel_Itinerary_${safeName}.pdf`;
      pdf.save(fileName);
      message.success(`Download started: ${fileName}`);
    } catch (err) {
      console.error(err);
      message.error('PDF generation failed');
    } finally {
      if (original) el.setAttribute('style', original); else el.removeAttribute('style');
      setCapturing(false);
      setDownloading(false);
    }
  };

  const info = [
    ['Traveller', travellerName],
    ['Passport No.', tourist.passportNumber],
    ['Nationality', tourist.nationality],
    ['Travel Period', itinerary ? `${fmtDate(itinerary.startDate, false)} – ${fmtDate(itinerary.endDate, false)}` : ''],
    ['Duration', days.length ? `${days.length} days` : ''],
  ].filter(([, v]) => v);

  return (
    <div style={{ background: '#e8e8e8', minHeight: '100vh' }}>
      <div
        className="no-print"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          background: '#1a1a2e', padding: isMobile ? '8px 12px' : '12px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} size={isMobile ? 'small' : 'middle'}>
          返回通知书
        </Button>
        <Space>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()} size={isMobile ? 'small' : 'middle'}>
            Print
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={downloading}
            onClick={handleDownload}
            size={isMobile ? 'small' : 'middle'}
          >
            Download PDF
          </Button>
        </Space>
      </div>

      <div style={{ padding: isMobile ? '56px 8px 16px' : '76px 16px 32px', display: 'flex', justifyContent: 'center' }}>
        <div
          ref={docRef}
          style={{
            width: '100%', maxWidth: 794, background: '#fff', boxSizing: 'border-box',
            padding: compact ? '20px 14px' : '40px 48px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            fontFamily: '-apple-system, "Segoe UI", Roboto, Arial, "PingFang SC", sans-serif',
            fontSize: compact ? 13 : 14, lineHeight: 1.6, color: '#333',
          }}
        >
          <div style={{ textAlign: 'center', borderBottom: `3px double ${C.primary}`, paddingBottom: 14, marginBottom: 18 }}>
            <div style={{ fontSize: compact ? 22 : 28, fontWeight: 700, color: C.primary, letterSpacing: 2 }}>
              TRAVEL ITINERARY
            </div>
            <div style={{ color: C.muted, marginTop: 4 }}>Worldway Travel</div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <tbody>
              {info.map(([k, v]) => (
                <tr key={k}>
                  <th style={{ ...head, width: compact ? 110 : 150 }}>{k}</th>
                  <td style={{ ...cell, wordBreak: 'break-word' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {flights.length > 0 && (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: '0 0 8px' }}>Flights</div>
              <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: compact ? 520 : 0 }}>
                  <thead>
                    <tr>
                      <th style={head}>Date</th>
                      <th style={head}>Flight</th>
                      <th style={head}>Route</th>
                      <th style={head}>Departure</th>
                      <th style={head}>Arrival</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flights.map((f, i) => {
                      const nextDay = f.arrivalDate && f.date && f.arrivalDate !== f.date;
                      return (
                        <tr key={i}>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>{f.date ? fmtDate(f.date, false) : ''}</td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>{f.flightNo || f.flight}</td>
                          <td style={cell}>{(f.fromCityEn || f.fromCity || f.from)} → {(f.toCityEn || f.toCity || f.to)}</td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>{f.time}</td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>{f.arrivalTime}{nextDay ? ' (+1)' : ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: '0 0 8px' }}>Daily Itinerary</div>
          {compact ? (
            days.map((d) => (
              <div key={d.dayNumber} style={{ border: `1px solid ${C.border}`, borderRadius: 4, marginBottom: 10 }}>
                <div style={{ background: C.headBg, padding: '6px 10px', color: C.primary, fontWeight: 600 }}>
                  Day {d.dayNumber} · {fmtDate(d.date)}
                </div>
                <div style={{ padding: '8px 10px' }}>
                  <DayBody day={d} />
                </div>
              </div>
            ))
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 70 }} />
                <col style={{ width: 120 }} />
                <col />
                <col style={{ width: 190 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={head}>Day</th>
                  <th style={head}>Date</th>
                  <th style={head}>Itinerary</th>
                  <th style={head}>Accommodation</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.dayNumber} style={{ breakInside: 'avoid' }}>
                    <td style={{ ...cell, fontWeight: 600, color: C.primary }}>Day {d.dayNumber}</td>
                    <td style={cell}>{fmtDate(d.date)}</td>
                    <td style={{ ...cell, wordBreak: 'break-word' }}><DayBody day={d} showStay={false} /></td>
                    <td style={{ ...cell, wordBreak: 'break-word' }}><Stay day={d} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 28, paddingTop: 12, borderTop: `1px solid ${C.border}`, textAlign: 'center', color: C.muted, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>Worldway Travel</div>
            <div>Tel: 0800 116 688 | Email: confirmation@worldway.co.nz</div>
          </div>
        </div>
      </div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

const Stay = ({ day }) => {
  if (day.hotel) {
    return (
      <div>
        <div style={{ fontWeight: 600 }}>{day.hotel.name}</div>
        {day.hotel.address && <div style={{ color: C.muted, fontSize: '0.92em' }}>{day.hotel.address}</div>}
        {day.hotel.phone && <div style={{ color: C.muted, fontSize: '0.92em' }}>Tel: {day.hotel.phone}</div>}
      </div>
    );
  }
  return day.accommodation ? <div>{day.accommodation}</div> : <span style={{ color: '#bbb' }}>-</span>;
};

const DayBody = ({ day, showStay = true }) => (
  <div>
    {day.title && <div style={{ fontWeight: 600 }}>{day.title}</div>}
    {day.route && <div style={{ color: C.muted, fontSize: '0.92em' }}>Transport: {day.route}</div>}
    {day.description && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{day.description}</div>}
    {showStay && (day.hotel || day.accommodation) && (
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${C.border}` }}>
        <span style={{ color: C.muted }}>Accommodation: </span>
        <Stay day={day} />
      </div>
    )}
  </div>
);

export default TravelItinerary;
